// Manager-override prompt (legacy POS: a higher-privileged clerk authorizes a
// restricted action with their PIN). Renders a clerk picker + PIN field and
// verifies the chosen clerk holds the required permission before approving.
import { useEffect, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { listClerks, verifyClerkOverride, type Clerk } from '../../services/data'
import { Button } from './ui'

export default function ManagerOverride({
  perm, title, message, onApproved, onClose,
}: {
  perm: string
  title?: string
  message?: string
  onApproved: (username: string) => void
  onClose: () => void
}) {
  const [clerks, setClerks] = useState<Clerk[]>([])
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    listClerks().then(cs => {
      setClerks(cs)
      if (cs.length) setEmail(cs[0].email)
    }).catch(() => {})
  }, [])

  const approve = async () => {
    if (!email || !pin) { setError('Select a clerk and enter a PIN.'); return }
    setBusy(true); setError('')
    try {
      const res = await verifyClerkOverride(email, pin, perm)
      if (res.ok) { onApproved(res.username ?? email) }
      else setError('That clerk is not authorized, or the PIN is incorrect.')
    } catch {
      setError('Verification failed. Try again.')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
          <ShieldAlert size={18} className="text-amber-600" />
          <h3 className="font-semibold text-gray-800">{title ?? 'Manager Authorization Required'}</h3>
        </div>
        <div className="p-6 space-y-4">
          {message && <p className="text-sm text-gray-600">{message}</p>}
          <label className="text-sm block">
            <span className="block text-gray-600 mb-1">Authorizing clerk</span>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2" value={email} onChange={e => setEmail(e.target.value)}>
              {clerks.map(c => <option key={c.email} value={c.email}>{c.username}</option>)}
            </select>
          </label>
          <label className="text-sm block">
            <span className="block text-gray-600 mb-1">PIN</span>
            <input type="password" autoComplete="off" autoFocus className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={pin} onChange={e => setPin(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') approve() }} placeholder="••••" />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={approve} disabled={busy}>{busy ? 'Verifying…' : 'Authorize'}</Button>
        </div>
      </div>
    </div>
  )
}
